<script setup>
import { computed, ref, watch } from 'vue'
import { defaultVTooltipProps } from '@/utils/defaultProps'

const props = defineProps({
  value: {
    type: Boolean,
    default: false,
  },
  buttonTitle: {
    type: String,
    default: null,
  },
  onButtonActivate: {
    type: Function,
    default: () => {},
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  visible: {
    type: Boolean,
    default: true,
  },
  title: {
    type: String,
    default: null,
  },
  maxWidth: {
    type: [String, Number],
    default: '420',
  },
  filters: {
    type: Object,
    required: true,
  },
  showImages: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['input', 'update:showImages', 'update:filters'])

const baseId = ref(`safety-appearance-settings-${Math.random().toString(36).slice(2, 8)}`)
const dialog = ref(false)
const localHideND = ref(true)

const showImagesModel = computed({
  get: () => props.showImages,
  set: (value) => emit('update:showImages', value),
})

const showChemicalsWithoutDataModel = computed({
  get: () => !localHideND.value,
  set: (value) => {
    localHideND.value = !value
    emit('update:filters', {
      ...props.filters,
      hideND: !value,
    })
  },
})

const activatorButtonId = computed(() => `${baseId.value}-button`)
const settingsChanged = computed(() => showImagesModel.value || showChemicalsWithoutDataModel.value)

watch(
  () => props.value,
  (value) => {
    dialog.value = value
  },
)

watch(dialog, (value) => {
  emit('input', value)
})

watch(
  () => props.filters.hideND,
  (value) => {
    localHideND.value = value ?? true
  },
  { immediate: true },
)
</script>

<template>
  <v-menu
    v-model="dialog"
    offset-y
    :close-on-content-click="false"
    :max-width="maxWidth"
  >
    <template #activator="{ on, attrs }">
      <v-btn
        v-if="buttonTitle"
        :id="activatorButtonId"
        class="border flat-button"
        v-bind="attrs"
        v-on="on"
        :disabled="disabled"
        :style="{ display: visible ? 'flex' : 'none' }"
        :aria-label="title"
        @click="onButtonActivate"
      >
        <template v-if="buttonTitle.startsWith('mdi-')">
          <v-badge
            v-if="settingsChanged"
            dot
            color="primary"
            offset-x="-4"
            offset-y="0"
          >
            <v-icon>
              {{ buttonTitle }}
            </v-icon>
          </v-badge>

          <v-icon v-else>
            {{ buttonTitle }}
          </v-icon>
        </template>

        <template v-else>
          {{ title }}
        </template>
      </v-btn>

      <v-tooltip
        v-if="title"
        top
        v-bind="defaultVTooltipProps"
        :activator="`#${activatorButtonId}`"
      >
        {{ title }}
      </v-tooltip>
    </template>

    <v-card class="settings-menu-card">
      <v-card-title>{{ title }}</v-card-title>

      <v-card-text class="settings-menu-card__content">
        <div class="d-flex flex-column gap-4">
          <div class="d-flex flex-column gap-1">
            <div class="font-weight-bold">Appearance</div>

            <v-switch
              v-model="showChemicalsWithoutDataModel"
              label="Show Empty"
              hide-details
              class="mt-0"
            />

            <v-switch
              v-model="showImagesModel"
              label="Show Structure"
              hide-details
              class="mt-0"
            />
          </div>
        </div>
      </v-card-text>

      <v-card-actions>
        <v-spacer />
        <v-btn
          aria-label="Close"
          @click="dialog = false"
        >
          Close
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-menu>
</template>

<style scoped>
.settings-menu-card {
  max-height: 80vh;
  display: flex;
  flex-direction: column;
}

.settings-menu-card__content {
  overflow-y: auto;
}
</style>
